/**
 * Preset de thème Arago pour PrimeVue 4
 * Définit les design tokens avec les couleurs Arago
 */

import { definePreset } from '@primevue/themes'
import Aura from '@primevue/themes/aura'

const AragoPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{red.50}',
      100: '{red.100}',
      200: '{red.200}',
      300: '{red.300}',
      400: '{red.400}',
      500: '#A41D1B',  // Couleur principale Arago
      600: '#8b1917',
      700: '#721513',
      800: '#59110f',
      900: '#400d0b',
      950: '{red.950}',
    },
    colorScheme: {
      light: {
        primary: {
          color: '#A41D1B',
          contrastColor: '#ffffff',
          hoverColor: '#8b1917',
          activeColor: '#721513',
        },
        highlight: {
          background: '#A41D1B',
          focusBackground: '#8b1917',
          color: '#ffffff',
          focusColor: '#ffffff',
        },
      },
      dark: {
        // Rouge Arago conservé, avec variantes plus claires pour meilleur contraste
        primary: {
          color: '#dc2a26', // Plus clair pour le texte/liens sur fond sombre
          contrastColor: '#111827',
          hoverColor: '#ef4444',
          activeColor: '#f87171',
        },
        highlight: {
          background: '#A41D1B',
          focusBackground: '#dc2a26',
          color: '#ffffff',
          focusColor: '#ffffff',
        },
        // Surfaces sombres - valeurs hautes adoucies
        surface: {
          0: '#111827',
          50: '#1f2937',
          100: '#374151',
          200: '#4b5563',
          300: '#6b7280',
          400: '#9ca3af',
          500: '#d1d5db',
          600: '#d1d5db',  // Plafonné pour éviter le blanc pur
          700: '#e5e7eb',
          800: '#e5e7eb',
          900: '#f3f4f6',  // Gris très clair, jamais blanc pur
        },
        // Texte adapté au mode sombre - gris clair pour réduire la fatigue visuelle
        text: {
          color: '#e5e7eb',      // Gris clair au lieu de quasi-blanc
          hoverColor: '#f3f4f6', // Légèrement plus clair au hover
          mutedColor: '#9ca3af', // Gris moyen pour texte secondaire
          hoverMutedColor: '#d1d5db',
        },
        // Contenu
        content: {
          background: '#1f2937',
          hoverBackground: '#374151',
          borderColor: '#4b5563',
          color: '#e5e7eb',      // Cohérent avec text.color
          hoverColor: '#f3f4f6', // Cohérent avec text.hoverColor
        },
      },
    },
  },
  components: {
    button: {
      colorScheme: {
        light: {
          primary: {
            background: '#A41D1B',
            hoverBackground: '#8b1917',
            activeBackground: '#721513',
            borderColor: '#A41D1B',
            hoverBorderColor: '#8b1917',
            activeBorderColor: '#721513',
            color: '#ffffff',
            hoverColor: '#ffffff',
            activeColor: '#ffffff',
          },
          secondary: {
            background: '#5a271f',
            hoverBackground: '#4a1f19',
            activeBackground: '#3a1713',
            borderColor: '#5a271f',
            hoverBorderColor: '#4a1f19',
            activeBorderColor: '#3a1713',
            color: '#ffffff',
            hoverColor: '#ffffff',
            activeColor: '#ffffff',
          },
          success: {
            background: '#74c174',
            hoverBackground: '#5fb05f',
            activeBackground: '#4a9f4a',
            borderColor: '#74c174',
            hoverBorderColor: '#5fb05f',
            activeBorderColor: '#4a9f4a',
            color: '#ffffff',
            hoverColor: '#ffffff',
            activeColor: '#ffffff',
          },
          info: {
            background: '#2D3085',
            hoverBackground: '#252770',
            activeBackground: '#1d1e5b',
            borderColor: '#2D3085',
            hoverBorderColor: '#252770',
            activeBorderColor: '#1d1e5b',
            color: '#ffffff',
            hoverColor: '#ffffff',
            activeColor: '#ffffff',
          },
          warn: {
            background: '#d08a48',
            hoverBackground: '#c07838',
            activeBackground: '#b06628',
            borderColor: '#d08a48',
            hoverBorderColor: '#c07838',
            activeBorderColor: '#b06628',
            color: '#ffffff',
            hoverColor: '#ffffff',
            activeColor: '#ffffff',
          },
          danger: {
            background: '#dc3545',
            hoverBackground: '#c82333',
            activeBackground: '#bd2130',
            borderColor: '#dc3545',
            hoverBorderColor: '#c82333',
            activeBorderColor: '#bd2130',
            color: '#ffffff',
            hoverColor: '#ffffff',
            activeColor: '#ffffff',
          },
        },
        dark: {
          primary: {
            background: '#A41D1B',
            hoverBackground: '#dc2a26',
            activeBackground: '#ef4444',
            borderColor: '#A41D1B',
            hoverBorderColor: '#dc2a26',
            activeBorderColor: '#ef4444',
            color: '#ffffff',
            hoverColor: '#ffffff',
            activeColor: '#ffffff',
          },
          secondary: {
            background: '#374151',
            hoverBackground: '#4b5563',
            activeBackground: '#6b7280',
            borderColor: '#4b5563',
            hoverBorderColor: '#6b7280',
            activeBorderColor: '#9ca3af',
            color: '#f9fafb',
            hoverColor: '#ffffff',
            activeColor: '#ffffff',
          },
          success: {
            background: '#22c55e',
            hoverBackground: '#16a34a',
            activeBackground: '#15803d',
            borderColor: '#22c55e',
            hoverBorderColor: '#16a34a',
            activeBorderColor: '#15803d',
            color: '#ffffff',
            hoverColor: '#ffffff',
            activeColor: '#ffffff',
          },
          info: {
            background: '#3b82f6',
            hoverBackground: '#2563eb',
            activeBackground: '#1d4ed8',
            borderColor: '#3b82f6',
            hoverBorderColor: '#2563eb',
            activeBorderColor: '#1d4ed8',
            color: '#ffffff',
            hoverColor: '#ffffff',
            activeColor: '#ffffff',
          },
          warn: {
            background: '#f59e0b',
            hoverBackground: '#d97706',
            activeBackground: '#b45309',
            borderColor: '#f59e0b',
            hoverBorderColor: '#d97706',
            activeBorderColor: '#b45309',
            color: '#ffffff',
            hoverColor: '#ffffff',
            activeColor: '#ffffff',
          },
          danger: {
            background: '#ef4444',
            hoverBackground: '#dc2626',
            activeBackground: '#b91c1c',
            borderColor: '#ef4444',
            hoverBorderColor: '#dc2626',
            activeBorderColor: '#b91c1c',
            color: '#ffffff',
            hoverColor: '#ffffff',
            activeColor: '#ffffff',
          },
        },
      },
    },
    datatable: {
      colorScheme: {
        light: {
          headerCellBackground: '#f8f9fa',
          headerCellBorderColor: '#dee2e6',
          headerCellColor: '#495057',
          bodyRowBackground: '#ffffff',
          bodyRowHoverBackground: '#f8f9fa',
          bodyRowSelectedBackground: '#fef2f2',
          bodyRowBorderColor: '#dee2e6',
          bodyCellBorderColor: '#dee2e6',
        },
        dark: {
          headerCellBackground: '#1f2937',
          headerCellBorderColor: '#374151',
          headerCellColor: '#f9fafb',
          bodyRowBackground: '#111827',
          bodyRowHoverBackground: '#1f2937',
          bodyRowSelectedBackground: '#7f1d1d',
          bodyRowBorderColor: '#374151',
          bodyCellBorderColor: '#374151',
        },
      },
    },
    card: {
      colorScheme: {
        light: {
          background: '#ffffff',
          borderColor: '#dee2e6',
          shadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
        dark: {
          background: '#1f2937',
          borderColor: '#374151',
          shadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
        },
      },
    },
  },
})

export default AragoPreset
